import { useEffect, useState, useCallback } from "react";
import { DataTable, ColumnDef, FilterOption } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Download } from "lucide-react";
import { api, ApiClientError, EmailTemplate } from "@/lib/api";

const PAGE_SIZE = 50;

export function EmailTemplateList() {
  const { logout } = useAuth();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [_totalCount, setTotalCount] = useState<number>(0);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.emailTemplates.list(PAGE_SIZE);
      setTemplates(data);
      setHasMore(data.length === PAGE_SIZE);
      setTotalCount(data.length);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to load templates");
        }
      } else {
        setError("Failed to load templates");
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, logout]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleNextPage = () => {
    if (!loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (!loading && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Define columns for the Email Templates table
  const columns: ColumnDef<EmailTemplate>[] = [
    {
      key: "template_name",
      header: "Template Name",
      render: (item) => (
        <div className="text-xs font-medium text-foreground">
          {item.template_name}
        </div>
      ),
      sortValue: (item) => item.template_name,
    },
    {
      key: "subject_line",
      header: "Subject Line",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.subject_line}
        </div>
      ),
      sortValue: (item) => item.subject_line,
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.type}</div>
      ),
      sortValue: (item) => item.type,
    },
    {
      key: "recipient_type",
      header: "Recipient Type",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {item.recipient_type}
        </div>
      ),
      sortValue: (item) => item.recipient_type,
    },
    {
      key: "usage_count",
      header: "Usage Count",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.usage_count}</div>
      ),
      sortValue: (item) => item.usage_count,
    },
    {
      key: "last_modified",
      header: "Last Modified",
      render: (item) => (
        <div className="text-xs text-muted-foreground">
          {new Date(item.last_modified).toLocaleDateString()}
        </div>
      ),
      sortValue: (item) => new Date(item.last_modified).getTime(),
    },
    {
      key: "modified_by",
      header: "Modified By",
      render: (item) => (
        <div className="text-xs text-muted-foreground">{item.modified_by}</div>
      ),
      sortValue: (item) => item.modified_by,
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      render: (item) => (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-3"
          onClick={() => {
            // TODO: Navigate to template editor or open modal
            console.log("View template:", item.id);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Define filters (for search functionality)
  const filters: FilterOption[] = [
    { label: "Type", placeholder: "All Types" },
    { label: "Recipient Type", placeholder: "All Recipients" },
  ];

  // Handle new template creation
  const handleNewTemplate = () => {
    // TODO: Implement new template modal or navigation
    console.log("Create new template");
  };

  // Handle export
  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export templates");
  };

  // Action buttons for the header
  const actionButtons = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        className="h-9 px-4"
      >
        <Download className="h-4 w-4 mr-2" />
        Export
      </Button>
      <Button
        size="sm"
        onClick={handleNewTemplate}
        className="h-9 px-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        New Template
      </Button>
    </>
  );

  return (
    <DataTable
      title="Email Templates"
      subtitle="Manage default email templates for takedown notices"
      columns={columns}
      data={templates}
      loading={loading}
      error={error}
      filters={filters}
      searchPlaceholder="Search templates..."
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      hasNextPage={hasMore}
      hasPreviousPage={currentPage > 1}
      currentPage={currentPage}
      itemsPerPage={PAGE_SIZE}
      actionButtons={actionButtons}
      totalCount={undefined}
      entityName="templates"
    />
  );
}


